import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Link2, Copy, Globe, MonitorPlay, QrCode, Radio, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

function LinkRow({ label, hint, path, testid }) {
  const url = `${window.location.origin}${path}`;
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#0F2547]">{label}</p>
        {hint && <p className="text-[11px] text-slate-400 truncate">{hint}</p>}
        <p className="text-[11px] text-[#005EB8] truncate">{url}</p>
      </div>
      <button onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }}
        data-testid={`${testid}-copy`}
        className="h-9 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5 transition-colors shrink-0">
        <Copy className="h-3.5 w-3.5" /> Copy
      </button>
    </div>
  );
}

export default function AdminLinks() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data.filter((e) => e.id && !e.archived))).catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="admin-links">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/admin")} data-testid="links-back"
            className="h-11 w-11 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors">
            <ChevronLeft className="h-5 w-5 text-[#0F2547]" />
          </button>
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#0F2547]">Useful links</h1>
            <p className="text-xs text-slate-500">Copy any link to share it</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
        <section className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="links-general">
          <h2 className="font-heading text-base font-bold text-[#0F2547] mb-2 flex items-center gap-2">
            <Link2 className="h-4 w-4 text-[#005EB8]" /> General
          </h2>
          <LinkRow label="New client intake form" hint="Send to a new client — fills your Client contacts + drops a quote job in To-Do" path="/intake" testid="link-intake" />
          <LinkRow label="New staff sign-up" hint="Send to new staff — they add their details, photo, DBS, licence & certificates" path="/join" testid="link-staff-join" />
          <LinkRow label="Client portal login" hint="For clients once you've created their login" path="/portal/login" testid="link-portal" />
          <LinkRow label="Staff login" hint="Your team's portal sign-in" path="/staff/login" testid="link-staff-login" />
          <LinkRow label="Control room sign-in" hint="Big-screen control room access" path="/control-login" testid="link-control-login" />
          <LinkRow label="Control room overview" hint="Live control room screen (needs the control password)" path="/control" testid="link-control" />
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="links-events">
          <h2 className="font-heading text-base font-bold text-[#0F2547] mb-1 flex items-center gap-2">
            <Radio className="h-4 w-4 text-[#005EB8]" /> Per event
          </h2>
          <p className="text-[11px] text-slate-400 mb-2">Live organiser view (no login, anonymised counters), control wall (big screen) and patient self check-in QR link.</p>
          {events.length === 0 ? (
            <p className="text-xs text-slate-400 py-2">No current events — add one on the Calendar and its links appear here.</p>
          ) : events.map((ev) => (
            <div key={ev.id} className="rounded-xl border border-slate-200 p-3 mb-2" data-testid={`links-event-${ev.id}`}>
              <p className="text-sm font-bold text-[#0F2547]">{ev.name} <span className="font-normal text-slate-400 text-xs">{ev.event_date}</span></p>
              {ev.share_token && (
                <LinkRow label="Live organiser view" path={`/live/${ev.share_token}`} testid={`link-live-${ev.id}`} />
              )}
              {ev.wall_token && (
                <LinkRow label="Control wall (big screen)" path={`/wall/${ev.wall_token}`} testid={`link-wall-${ev.id}`} />
              )}
              {ev.checkin_token && (
                <LinkRow label="Patient self check-in" path={`/checkin/${ev.checkin_token}`} testid={`link-checkin-${ev.id}`} />
              )}
            </div>
          ))}
        </section>
      </main>
    </div>
  );
}
